import { useCallback, useEffect, useMemo, useState } from "react";
import { useFilters, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch } from "../../../../state/store";
import { clearSelectedTableRowsIds, setSelectedTableRowsIds } from "../../../../state/ducks";

import { strCode, mercuryDevices } from "../../../../consts";
import { t } from "../../../../services";
import { useTableData } from "../../../../hooks";

import {
  DropdownInput,
  FlexContainers,
  SearchInput,
  Table,
  WrapperContainer
} from "../../../../components";

import { GenerateTableColumns } from "../components/deviceTableColumns/GenerateTableColumns";
import { DownloadRevokeTableColumns } from "../components/deviceTableColumns/DownloadRevokeTableColumns";
import { SelectDevicesStepWrapper } from "./styled";

const deviceTypeOptions = [
  {
    id: 0,
    value: strCode.ALL_DEVICES_LABEL
  },
  {
    id: 1,
    value: mercuryDevices.MERCURY_L_LB_C
  },
  {
    id: 2,
    value: mercuryDevices.MERCURY_L_LCD_C
  },
  {
    id: 3,
    value: mercuryDevices.MERCURY_W_LB_C
  },
  {
    id: 4,
    value: mercuryDevices.MERCURY_W_LCD_C
  }
];

const SelectDevicesStep = ({ deviceData, isGenerating, loading }) => {
  const selectedTableRowsIds = useSelector((state) => state.global.selectedTableRowsIds);

  const [searchValue, setSearchValue] = useState("");
  const [selectedDeviceType, setSelectedDeviceType] = useState(deviceTypeOptions[0]);

  const columns = useMemo(
    () => (isGenerating ? GenerateTableColumns : DownloadRevokeTableColumns),
    []
  );

  const devicesMapCallback = (device) => {
    return { ...device, id: device?.dusn };
  };

  const data = useTableData({
    loading,
    data: deviceData,
    loadingPlaceholdersRows: 8,
    mapCallback: devicesMapCallback
  });

  const checkIsRowSelected = useCallback(
    (id) => selectedTableRowsIds.includes(id),
    [selectedTableRowsIds]
  );

  const tableInstance = useTable(
    {
      columns,
      data
    },
    useFilters,
    useSortBy
  );

  const { setFilter, rows } = tableInstance;

  const { isAllSelected, selectedLength } = useMemo(() => {
    return {
      isAllSelected: selectedTableRowsIds.length === rows.length,
      selectedLength: selectedTableRowsIds.length
    };
  }, [selectedTableRowsIds, data]);

  const onAllCheck = (rows) => {
    const result = isAllSelected ? [] : selectedLength === 0 ? rows : [];
    dispatch(setSelectedTableRowsIds(result.map((row) => row.original.id)));
  };

  const onRowCheck = (deviceId) => {
    if (checkIsRowSelected(deviceId)) {
      dispatch(setSelectedTableRowsIds(selectedTableRowsIds.filter((id) => id !== deviceId)));
    } else {
      dispatch(setSelectedTableRowsIds([...selectedTableRowsIds, deviceId]));
    }
  };

  useEffect(() => {
    setFilter("dusn", searchValue);
  }, [searchValue]);

  useEffect(() => {
    setFilter("deviceName", selectedDeviceType.id ? selectedDeviceType.value : null);
  }, [selectedDeviceType]);

  useEffect(() => {
    return () => dispatch(clearSelectedTableRowsIds());
  }, []);

  return (
    <SelectDevicesStepWrapper isGenerating={isGenerating}>
      <WrapperContainer Container={FlexContainers.FlexRowStart} width="100%" gap={8}>
        <SearchInput
          id="device-search-input"
          value={searchValue}
          setValue={setSearchValue}
          placeholder={t(strCode.SEARCH_PLACEHOLDER)}
        />

        <WrapperContainer width={300}>
          <DropdownInput
            id="device-type-selector"
            selectedOption={selectedDeviceType}
            options={deviceTypeOptions}
            onSelectOption={(id) =>
              setSelectedDeviceType(deviceTypeOptions.find((option) => option.id === id))
            }
            disabled={loading}
            width={300}
          />
        </WrapperContainer>
      </WrapperContainer>

      <Table
        tableInstance={tableInstance}
        maxTableHeight="190px"
        searchedText={searchValue}
        searchInfo={t(strCode.NO_DEVICES_FOUND, { dusn: searchValue })}
        searchCheckInfo={t(strCode.CHECK_DEVICE_NUMBER_INFO)}
        onRowClick={(device) => onRowCheck(device.dusn)}
        callbackFn1={() => {
          return { isAllSelected, selectedLength };
        }}
        callbackFn2={onAllCheck}
        callbackFn3={checkIsRowSelected}
        callbackFn4={onRowCheck}
        maxVisibleRows={1}
        isLoading={loading}
        modalTable
        showTableBorder
      />
    </SelectDevicesStepWrapper>
  );
};

SelectDevicesStep.propTypes = {
  isGenerating: PropTypes.bool.isRequired,
  deviceData: PropTypes.arrayOf(
    PropTypes.shape({
      asset_id: PropTypes.string,
      codentify: PropTypes.string,
      deviceImage: PropTypes.string,
      deviceModel: PropTypes.string,
      deviceName: PropTypes.string,
      device_model: PropTypes.string,
      dusn: PropTypes.string,
      isMercuryDevice: PropTypes.bool,
      latest_firmware_version: PropTypes.string,
      latest_firmware_version_update_datetime: PropTypes.string,
      material_brand_family: PropTypes.string,
      material_color: PropTypes.string,
      material_group: PropTypes.string,
      material_model_number: PropTypes.string,
      type: PropTypes.string
    })
  ).isRequired,
  loading: PropTypes.bool
};

export default SelectDevicesStep;
